import { useCallback, useEffect, useMemo, useState } from "react";
import { Info, Check, Minus, X, CircleHelp, AlertTriangle, ArrowRight } from "lucide-react";
import { listRecommendationCandidates, approveMatchingRecommendation } from "../matchingRecommendation/matchingRecommendationApi.js";
import { initialsOf } from "./matchingWorkspaceModel.js";
import "./aiMatching.css";

/**
 * The matching console for one job.
 *
 * EVERY NUMBER ON THIS PANEL IS THE SERVER'S. The per-candidate score, the
 * parts it breaks into, their maximum weights, the score distribution, the
 * readiness funnel, the blocker tallies and how many candidates were analysed
 * all arrive from GET .../matching-recommendations/jobs/{jobId}/candidates.
 * Nothing here is computed a second time in the browser, and nothing is
 * padded when the answer is small: one ready job means one row, and a factor
 * worth 0 points is drawn at 0.
 *
 * WHAT THE ENGINE IS. A deterministic weighted sum over stored facts -
 * skill fit out of 60, readiness out of 30, location fit out of 10 - defined
 * once in MatchingRecommendationScoring.cs. It has no learned model and no
 * randomness, so the same data always produces the same ranking. The panel
 * says so on screen rather than letting a confident-looking score imply more
 * than the system does, and it never prints a model name or version, because
 * there is no model to name.
 *
 * The bars are the real parts, drawn against their real maximums, so a bar
 * that looks half full IS half of that factor's weight - the reader can add
 * the three up and land on the total printed beside them.
 */

const ENGINE_NOTE =
  "ให้คะแนนจากกฎที่กำหนดไว้ (ทักษะ ความพร้อม และพื้นที่) ผลเหมือนเดิมทุกครั้งกับข้อมูลชุดเดียวกัน ไม่ใช่โมเดลที่เรียนรู้เอง และไม่มีการสุ่ม";
const TOP_MATCH_PAGE_SIZE = 20;
// How many of the ranked candidates the right-hand column shows at once. The
// rest are reachable by the same list's own paging on the server; this is a
// display window, and the panel says what the real total is beside it.
const TOP_MATCH_VISIBLE = 8;

/**
 * Device/SIM binding is NOT a matching-readiness fact and this panel never
 * reports it. A worker is issued a phone and a SIM once they are placed, so
 * every candidate still waiting to be placed is unbound - ranking on it marked
 * the ordinary pre-placement state as a gap. MatchingRecommendationScoring has
 * dropped it from the score, the signals and the blockers; these two sets are
 * the belt to that braces, so a backend build that has not been redeployed yet
 * cannot put the retired row back on screen.
 */
const RETIRED_SIGNAL_KEYS = new Set(["tracking"]);
const RETIRED_BLOCKER_CODES = new Set(["TrackingNotReady"]);

export function readinessSignalsOf(candidate) {
  return (candidate?.signals ?? []).filter((signal) => signal && !RETIRED_SIGNAL_KEYS.has(signal.key));
}

export function liveBlockersOf(blockers) {
  return (blockers ?? []).filter((blocker) => blocker && !RETIRED_BLOCKER_CODES.has(blocker.code ?? blocker.key));
}

function percent(value, total) {
  if (!total) {
    return 0;
  }
  return Math.round((value / total) * 100);
}

// Status is never carried by colour alone (PRODUCT.md): each verdict gets its
// own glyph and keeps the server's own sentence beside it.
const SIGNAL_GLYPH = { Ready: Check, Partial: Minus, NotReady: X, Unknown: CircleHelp };

const SEND_ACTION_LABEL = "ยืนยันส่งให้นายจ้าง";

/**
 * Whether this row may be sent to the employer at all.
 *
 * Three separate refusals, all the server's: the account may not approve on
 * this line, this particular candidate is not eligible, or they have already
 * been sent. The tick box asks the same question the single-candidate button
 * asks, so the two can never disagree about who is sendable.
 */
export function canSend(candidate, board) {
  return (
    board?.canApprove === true &&
    candidate?.actions?.canApproveForEmployer === true &&
    candidate?.isApproved !== true
  );
}

function signalTone(code) {
  return SIGNAL_GLYPH[code] ? code.toLowerCase() : "unknown";
}

/**
 * One candidate's readiness, as a checklist rather than a wrap of chips.
 *
 * The old treatment squeezed a label and a full Thai sentence into a 10px pill
 * and let four of them wrap, so the one line that mattered - which check is not
 * met - was the hardest thing on the panel to find. Here every check is a row
 * with a fixed glyph gutter, the checks that are NOT met read loudest, and the
 * heading carries the count so the block can be read without reading it.
 *
 * Every word is the server's: signal.label and signal.statusText verbatim. The
 * only thing computed here is how many of the rows on screen are met, which is
 * a count of this candidate's complete signal set, not a sample of a list.
 */
function ReadinessChecklist({ signals, completenessPercent }) {
  if (!signals || signals.length === 0) {
    return null;
  }

  const metCount = signals.filter((signal) => signal.code === "Ready").length;

  return (
    <section className="wx-mw-ai-readiness">
      <header className="wx-mw-ai-readiness-head">
        <h3>ความพร้อม</h3>
        <span className="wx-mw-ai-readiness-count">
          <b>{metCount}</b> จาก {signals.length} ข้อ
        </span>
      </header>
      <ul className="wx-mw-ai-readiness-list">
        {signals.map((signal) => {
          const tone = signalTone(signal.code);
          const Glyph = SIGNAL_GLYPH[signal.code] ?? CircleHelp;
          // The profile check is the one signal with a magnitude rather than a
          // yes/no, and it is worth 10 of the 30 readiness points, so it shows
          // that magnitude instead of only naming it.
          const meter = signal.key === "profile" && typeof completenessPercent === "number" ? completenessPercent : null;
          return (
            <li key={signal.key} className={`wx-mw-ai-check wx-mw-ai-check--${tone}`}>
              <span className="wx-mw-ai-check-glyph" aria-hidden="true">
                <Glyph size={12} strokeWidth={3} />
              </span>
              <span className="wx-mw-ai-check-label">{signal.label}</span>
              {meter !== null && <span className="wx-mw-ai-check-value">{meter}%</span>}
              {/* The profile row's status IS the figure and the bar, so it does
                  not also repeat the server's "ครบ 88%" sentence underneath. */}
              {meter === null && <span className="wx-mw-ai-check-status">{signal.statusText ?? signal.code}</span>}
              {meter !== null && (
                <span className="wx-mw-ai-check-meter">
                  <span style={{ width: `${Math.min(Math.max(meter, 0), 100)}%` }} />
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** A candidate's parts, guaranteed to be an array of usable rows. */
export function scorePartsOf(candidate) {
  return (candidate?.scoreParts ?? []).filter((part) => part && typeof part.maxScore === "number" && part.maxScore > 0);
}

/**
 * The share of analysed candidates the server judged ready to offer, taken
 * from the LAST step of its own readiness funnel over the FIRST step - never
 * from the page of rows on screen, which is a window, not the population.
 */
export function readyRateOf(summary) {
  const funnel = summary?.readinessFunnel ?? [];
  if (funnel.length < 2) {
    return null;
  }
  const first = funnel[0]?.count ?? 0;
  const last = funnel[funnel.length - 1]?.count ?? 0;
  if (!first) {
    return null;
  }
  return { ready: last, of: first, percent: percent(last, first) };
}

export default function AiMatchingPanel({ job, jobId }) {
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [approve, setApprove] = useState({ status: "idle", workerId: null, message: null });
  const [picked, setPicked] = useState(() => new Set());
  const [batch, setBatch] = useState({ status: "idle", sent: 0, failures: [] });

  const load = useCallback(() => {
    let cancelled = false;
    setState((current) => ({ status: "loading", data: current.data, error: null }));
    listRecommendationCandidates(jobId, { page: 1, pageSize: TOP_MATCH_PAGE_SIZE })
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", data: null, error });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(load, [load]);

  const data = state.data;
  const items = useMemo(() => data?.items ?? [], [data]);
  const summary = data?.summary ?? null;

  // The top match is the opening selection, because the panel below is an
  // explanation and an explanation of nothing is a blank column.
  useEffect(() => {
    if (items.length > 0 && !items.some((item) => item.workerId === selectedWorkerId)) {
      setSelectedWorkerId(items[0].workerId);
    }
  }, [items, selectedWorkerId]);

  const selected = items.find((item) => item.workerId === selectedWorkerId) ?? null;
  const readyRate = readyRateOf(summary);

  const visible = useMemo(() => items.slice(0, TOP_MATCH_VISIBLE), [items]);
  const sendable = useMemo(() => visible.filter((item) => canSend(item, data)), [visible, data]);
  // Only ever the rows still on screen and still sendable: a tick left behind
  // by a reload would otherwise send somebody the operator can no longer see.
  const pickedIds = useMemo(
    () => sendable.filter((item) => picked.has(item.workerId)).map((item) => item.workerId),
    [sendable, picked],
  );

  // A different job is a different shortlist; carrying ticks across would send
  // the previous job's picks to this one's employer.
  useEffect(() => {
    setPicked(new Set());
    setBatch({ status: "idle", sent: 0, failures: [] });
  }, [jobId]);

  function togglePicked(workerId) {
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(workerId)) {
        next.delete(workerId);
      } else {
        next.add(workerId);
      }
      return next;
    });
  }

  async function handleApprove(workerId) {
    setApprove({ status: "submitting", workerId, message: null });
    try {
      await approveMatchingRecommendation({ jobId, workerId });
      setApprove({ status: "idle", workerId: null, message: null });
      load();
    } catch (error) {
      setApprove({
        status: "error",
        workerId,
        message: error?.body?.message ?? "ส่งอนุมัติไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
      });
    }
  }

  /**
   * Sends every ticked candidate, one real POST each.
   *
   * Deliberately sequential and deliberately not all-or-nothing: this API has
   * no batch route and no transaction across rows, so the only honest report is
   * per worker. One refusal (already approved, no longer eligible) must not
   * discard the sends that did succeed, and the bar names who failed and why in
   * the server's own words rather than collapsing the batch to "ไม่สำเร็จ".
   */
  async function handleSendPicked() {
    const targets = pickedIds
      .map((workerId) => items.find((item) => item.workerId === workerId))
      .filter(Boolean);
    if (targets.length === 0) {
      return;
    }

    setBatch({ status: "sending", sent: 0, failures: [] });
    const failures = [];
    let sent = 0;
    for (const target of targets) {
      try {
        await approveMatchingRecommendation({ jobId, workerId: target.workerId });
        sent += 1;
      } catch (error) {
        failures.push({
          workerId: target.workerId,
          fullName: target.fullName ?? target.workerCode ?? target.workerId,
          message: error?.body?.message ?? "ส่งไม่สำเร็จ",
        });
      }
    }

    setBatch({ status: "done", sent, failures });
    // Only the ones that actually went through lose their tick, so a failed row
    // stays selected and can be retried without being found again.
    setPicked(new Set(failures.map((failure) => failure.workerId)));
    load();
  }

  if (state.status === "loading" && !data) {
    return <p className="wx-mw-muted">กำลังคำนวณคู่ที่แนะนำ...</p>;
  }
  if (state.status === "error") {
    return (
      <div className="wx-mw-ai-state" role="alert">
        <h3>โหลดผลการจับคู่ไม่สำเร็จ</h3>
        <p>ติดต่อระบบไม่ได้ หรือบัญชีของคุณไม่มีสิทธิ์ดูงานนี้</p>
        <button type="button" className="wx-button wx-button--ghost" onClick={load}>
          ลองใหม่
        </button>
      </div>
    );
  }

  return (
    <div className="wx-mw-ai">
      {/* --- Overview: what was matched against what --- */}
      <section className="wx-mw-ai-overview">
        <header className="wx-mw-ai-head">
          <h2>ภาพรวมการจับคู่</h2>
          <span className="wx-mw-ai-engine" title={ENGINE_NOTE}>
            <Info size={13} aria-hidden="true" />
            เกณฑ์การให้คะแนน
          </span>
        </header>

        <div className="wx-mw-ai-flow">
          <div className="wx-mw-ai-side">
            <p className="wx-mw-ai-side-label">งาน (Job Demand)</p>
            <p className="wx-mw-ai-side-value">{(job?.requiredWorkerCount ?? 0).toLocaleString("th-TH")}</p>
            <p className="wx-mw-ai-side-note">ตำแหน่งที่ต้องการ</p>
            {job?.matchedWorkerCount != null && (
              <p className="wx-mw-ai-side-note">จับคู่แล้ว {job.matchedWorkerCount.toLocaleString("th-TH")}</p>
            )}
          </div>

          <div className="wx-mw-ai-core">
            <p className="wx-mw-ai-core-title">โมเดลจับคู่</p>
            <ul className="wx-mw-ai-factorkeys">
              {/* The factor names and their weights come from the parts the
                  server returned for the top candidate - the same three parts
                  it scores every candidate on. */}
              {scorePartsOf(items[0]).map((part) => (
                <li key={part.key}>
                  <Check size={12} aria-hidden="true" />
                  {part.label}
                  <span>{part.maxScore} คะแนน</span>
                </li>
              ))}
            </ul>
            {readyRate && (
              <p className="wx-mw-ai-rate">
                <strong>{readyRate.percent}%</strong> ของผู้ที่วิเคราะห์แล้วพร้อมเสนอนายจ้าง ({readyRate.ready.toLocaleString("th-TH")} จาก{" "}
                {readyRate.of.toLocaleString("th-TH")} คน)
              </p>
            )}
          </div>

          <div className="wx-mw-ai-side">
            <p className="wx-mw-ai-side-label">แรงงาน (Worker Supply)</p>
            <p className="wx-mw-ai-side-value">{(summary?.totalCandidates ?? 0).toLocaleString("th-TH")}</p>
            <p className="wx-mw-ai-side-note">ผู้สมัครที่มองเห็นได้</p>
            {summary?.isTruncated && (
              <p className="wx-mw-ai-side-note wx-mw-ai-side-note--warn">
                วิเคราะห์ละเอียด {summary.analyzedCandidates?.toLocaleString("th-TH")} คนแรก
              </p>
            )}
          </div>
        </div>

        {summary?.scoreDistribution?.length > 0 && (
          <div className="wx-mw-ai-dist">
            <h3>การกระจายคะแนน</h3>
            <div className="wx-mw-ai-dist-bars">
              {summary.scoreDistribution.map((bucket) => {
                const max = Math.max(...summary.scoreDistribution.map((entry) => entry.count), 1);
                return (
                  <div key={bucket.key} className="wx-mw-ai-dist-col">
                    <span className="wx-mw-ai-dist-count">{bucket.count.toLocaleString("th-TH")}</span>
                    <span className="wx-mw-ai-dist-bar" style={{ height: `${Math.round((bucket.count / max) * 100)}%` }} />
                    <span className="wx-mw-ai-dist-label">{bucket.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {liveBlockersOf(summary?.topBlockers).length > 0 && (
          <div className="wx-mw-ai-blockers">
            <h3>อุปสรรคที่พบมากที่สุด</h3>
            <ul>
              {liveBlockersOf(summary?.topBlockers).map((blocker) => (
                <li key={blocker.key}>
                  <AlertTriangle size={12} aria-hidden="true" />
                  {blocker.label}
                  <span>{blocker.count.toLocaleString("th-TH")} คน</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="wx-mw-ai-note">{ENGINE_NOTE}</p>
      </section>

      {/* --- Ranked candidates --- */}
      <section className="wx-mw-ai-matches">
        <header className="wx-mw-ai-head">
          <h2>แรงงานที่แนะนำ</h2>
          <span className="wx-mw-ai-count">{(summary?.totalCandidates ?? items.length).toLocaleString("th-TH")} รายชื่อ</span>
        </header>

        {/* Sending several at once is the ordinary case: a job asks for twenty
            people, and approving them one row at a time was twenty round trips
            through the same decision. The bar only appears when there is at
            least one row this account may actually send. */}
        {sendable.length > 0 && (
          <div className="wx-mw-ai-batch" data-testid="batch-send-bar">
            <label className="wx-mw-ai-batch-all">
              <input
                type="checkbox"
                checked={pickedIds.length === sendable.length}
                disabled={batch.status === "sending"}
                onChange={() =>
                  setPicked(
                    pickedIds.length === sendable.length
                      ? new Set()
                      : new Set(sendable.map((item) => item.workerId)),
                  )
                }
              />
              เลือกทั้งหมด
            </label>
            {/* Outside the label on purpose: text inside it joins the tick
                box's accessible name, so "เลือกทั้งหมด" would be announced as
                "เลือกทั้งหมด (5 คนที่ส่งได้)". */}
            <span className="wx-mw-ai-batch-eligible">({sendable.length.toLocaleString("th-TH")} คนที่ส่งได้)</span>

            <span className="wx-mw-ai-batch-count">
              {pickedIds.length === 0 ? "ยังไม่ได้เลือกใคร" : `เลือกไว้ ${pickedIds.length.toLocaleString("th-TH")} คน`}
            </span>

            {pickedIds.length > 0 && batch.status !== "sending" && (
              <button type="button" className="wx-button wx-button--ghost" onClick={() => setPicked(new Set())}>
                ล้างที่เลือก
              </button>
            )}
            <button
              type="button"
              className="wx-button wx-button--primary"
              disabled={pickedIds.length === 0 || batch.status === "sending"}
              onClick={handleSendPicked}
            >
              {batch.status === "sending"
                ? "กำลังส่ง..."
                : `${SEND_ACTION_LABEL}${pickedIds.length > 0 ? ` (${pickedIds.length.toLocaleString("th-TH")})` : ""}`}
              <ArrowRight size={15} aria-hidden="true" />
            </button>
          </div>
        )}

        {batch.status === "done" && (
          <div className="wx-mw-ai-batch-result" role="status">
            {batch.sent > 0 && (
              <p className="wx-mw-ai-batch-sent">
                <Check size={14} aria-hidden="true" /> ส่งให้นายจ้างแล้ว {batch.sent.toLocaleString("th-TH")} คน
                {/* Said plainly, because the tab it moves to is named for a
                    confirmation the platform cannot yet collect. */}
                <em>รอนายจ้างยืนยัน - ดูที่แท็บ ยืนยันโดยนายจ้าง</em>
              </p>
            )}
            {batch.failures.length > 0 && (
              <div className="wx-mw-ai-batch-failed">
                <p>ส่งไม่สำเร็จ {batch.failures.length.toLocaleString("th-TH")} คน</p>
                <ul>
                  {batch.failures.map((failure) => (
                    <li key={failure.workerId}>
                      <strong>{failure.fullName}</strong> {failure.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {items.length === 0 ? (
          <p className="wx-mw-muted">ยังไม่มีแรงงานที่เข้าเกณฑ์ของงานนี้</p>
        ) : (
          <>
            <ol className="wx-mw-ai-list">
              {visible.map((item, index) => {
                const isSelected = item.workerId === selectedWorkerId;
                const selectable = canSend(item, data);
                const isPicked = selectable && picked.has(item.workerId);
                const who = item.fullName ?? item.workerCode ?? item.workerId;
                return (
                  <li key={item.workerId} className={`wx-mw-ai-item${isPicked ? " wx-mw-ai-item--picked" : ""}`}>
                    {/* Outside the row button, never inside it: a control nested
                        in a control is neither clickable nor announced. */}
                    <span className="wx-mw-ai-pick">
                      {item.isApproved ? (
                        <Check size={14} className="wx-mw-ai-pick-sent" aria-label={`${who} ส่งให้นายจ้างแล้ว`} />
                      ) : (
                        <input
                          type="checkbox"
                          checked={isPicked}
                          disabled={!selectable || batch.status === "sending"}
                          onChange={() => togglePicked(item.workerId)}
                          aria-label={`เลือก ${who}`}
                          // The server's own refusal, so a box that cannot be
                          // ticked says why on hover instead of just resisting.
                          title={selectable ? undefined : item.actions?.blockedReason ?? undefined}
                        />
                      )}
                    </span>
                    <button
                      type="button"
                      className={`wx-mw-ai-row${isSelected ? " wx-mw-ai-row--selected" : ""}`}
                      aria-pressed={isSelected}
                      onClick={() => setSelectedWorkerId(item.workerId)}
                    >
                      <span className="wx-mw-ai-rank">{index + 1}</span>
                      <span className="wx-mw-ai-avatar" aria-hidden="true">
                        {initialsOf(item.fullName)}
                      </span>
                      <span className="wx-mw-ai-who">
                        <strong>{item.fullName ?? "-"}</strong>
                        <em>{item.workerCode ?? item.workerId}</em>
                        <em>{item.nationality ?? "-"}</em>
                      </span>
                      <span className="wx-mw-ai-score">
                        <b>{item.matchScore}</b>
                        <em>{item.matchLevelLabel ?? item.matchLevel ?? ""}</em>
                      </span>
                      <span className="wx-mw-ai-minibars">
                        {scorePartsOf(item).map((part) => (
                          <span key={part.key} className="wx-mw-ai-minibar" title={`${part.label} ${part.score}/${part.maxScore}`}>
                            <span style={{ width: `${percent(part.score, part.maxScore)}%` }} />
                          </span>
                        ))}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            {items.length > TOP_MATCH_VISIBLE && (
              <p className="wx-mw-ai-more">
                แสดง {TOP_MATCH_VISIBLE} อันดับแรกจาก {items.length.toLocaleString("th-TH")} รายชื่อที่โหลดมา
              </p>
            )}
          </>
        )}
      </section>

      {/* --- Why this one --- */}
      {selected && (
        <section className="wx-mw-ai-why">
          <header className="wx-mw-ai-head">
            <h2>เหตุผลของคะแนน</h2>
            <span className="wx-mw-ai-count">{selected.fullName ?? "-"}</span>
          </header>

          <div className="wx-mw-ai-why-grid">
            <div className="wx-mw-ai-total">
              <span className="wx-mw-ai-total-value">{selected.matchScore}</span>
              <span className="wx-mw-ai-total-label">คะแนนรวม</span>
              <span className={`wx-mw-ai-level wx-mw-ai-level--${(selected.matchLevel ?? "").toLowerCase()}`}>
                {selected.matchLevelLabel ?? selected.matchLevel ?? "-"}
              </span>
            </div>

            <div className="wx-mw-ai-factors">
              {scorePartsOf(selected).map((part) => (
                <div key={part.key} className="wx-mw-ai-factor">
                  <div className="wx-mw-ai-factor-head">
                    <strong>{part.label}</strong>
                    <span>
                      {part.score}/{part.maxScore}
                    </span>
                  </div>
                  <span className="wx-mw-ai-factor-bar">
                    <span style={{ width: `${percent(part.score, part.maxScore)}%` }} />
                  </span>
                  {part.detail && <p>{part.detail}</p>}
                </div>
              ))}
            </div>

            <div className="wx-mw-ai-facts">
              <div className="wx-mw-ai-fact">
                <span>ทักษะที่ตรง</span>
                <strong>
                  {selected.matchedSkillNames?.length > 0 ? selected.matchedSkillNames.join(", ") : "ไม่มีทักษะที่ตรงกับที่งานต้องการ"}
                </strong>
              </div>
              {selected.missingSkillNames?.length > 0 && (
                <div className="wx-mw-ai-fact">
                  <span>ทักษะที่ยังขาด</span>
                  <strong className="wx-mw-ai-fact--warn">{selected.missingSkillNames.join(", ")}</strong>
                </div>
              )}
              {/* Profile completeness is not repeated as its own fact row: the
                  checklist below states it with its verdict and a meter. */}
              <ReadinessChecklist
                signals={readinessSignalsOf(selected)}
                completenessPercent={selected.profileCompletenessPercent}
              />
            </div>

            <div className="wx-mw-ai-actions">
              <h3>ส่งให้นายจ้าง</h3>
              {selected.isApproved ? (
                <p className="wx-mw-ai-approved">
                  <Check size={14} aria-hidden="true" /> ส่งให้นายจ้างแล้ว
                </p>
              ) : (
                <>
                  {/* Same words as the batch bar: one action must not have two
                      names depending on which control the operator reached. */}
                  <button
                    type="button"
                    className="wx-button wx-button--primary"
                    disabled={!canSend(selected, data) || approve.status === "submitting"}
                    onClick={() => handleApprove(selected.workerId)}
                  >
                    {approve.status === "submitting" && approve.workerId === selected.workerId
                      ? "กำลังส่ง..."
                      : SEND_ACTION_LABEL}
                    <ArrowRight size={15} aria-hidden="true" />
                  </button>
                  {/* The server's own reason, verbatim - never a guess made here. */}
                  {selected.actions?.blockedReason && <p className="wx-mw-ai-blocked">{selected.actions.blockedReason}</p>}
                  {data?.canApprove !== true && (
                    <p className="wx-mw-ai-blocked">บัญชีของคุณดูรายการนี้ได้อย่างเดียว การอนุมัติทำโดย SysAdmin เท่านั้น</p>
                  )}
                </>
              )}
              {approve.status === "error" && approve.workerId === selected.workerId && (
                <p className="wx-mw-ai-blocked" role="alert">
                  {approve.message}
                </p>
              )}
              {liveBlockersOf(selected.blockers).length > 0 && (
                <ul className="wx-mw-ai-warnlist">
                  {liveBlockersOf(selected.blockers).map((blocker) => (
                    <li key={blocker.code}>{blocker.message ?? blocker.code}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
